/**
 * Simplified Thumbnail Service
 * Direct image loading without complex caching or preloading
 */

const THUMBNAIL_BASE_PATH = '/assets/thumbnails/';
const SUPPORTED_EXTENSIONS = ['png', 'jpg', 'jpeg'];

// Simple cache to prevent multiple simultaneous requests
const imageExistenceCache = new Map<string, Promise<boolean>>();

/**
 * Robust image existence check with timeout and caching
 */
const checkImageExists = (url: string): Promise<boolean> => {
  // Return cached promise if already checking this URL
  if (imageExistenceCache.has(url)) {
    return imageExistenceCache.get(url)!;
  }
  
  const promise = new Promise<boolean>((resolve) => {
    const img = new Image();
    let resolved = false;
    
    const cleanup = () => {
      if (!resolved) {
        resolved = true;
        img.onload = null;
        img.onerror = null;
        // Remove from cache after completion
        imageExistenceCache.delete(url);
      }
    };
    
    // Set a timeout to prevent hanging
    const timeout = setTimeout(() => {
      cleanup();
      resolve(false);
    }, 3000); // 3 second timeout
    
    img.onload = () => {
      cleanup();
      clearTimeout(timeout);
      resolve(true);
    };
    
    img.onerror = () => {
      cleanup();
      clearTimeout(timeout);
      resolve(false);
    };
    
    // Set crossOrigin to handle CORS issues
    img.crossOrigin = 'anonymous';
    img.src = url;
  });
  
  // Cache the promise
  imageExistenceCache.set(url, promise);
  return promise;
};

/**
 * Find thumbnail path for a given ID and type
 */
const findThumbnailPath = async (id: string, type: 'article' | 'project'): Promise<string | null> => {
  const mappingKey = type === 'article' 
    ? (THUMBNAIL_MAPPING[id] || id)
    : (PROJECT_THUMBNAIL_MAPPING[id] || id);
  
  const suffix = type === 'article' ? '-article' : '-project';

  // For projects, check if we have a known mapping first (faster path)
  if (type === 'project' && PROJECT_THUMBNAIL_MAPPING[id]) {
    // We know this project should have a thumbnail, so try PNG first (most common)
    const pngPath = `${THUMBNAIL_BASE_PATH}${mappingKey}${suffix}.png`;
    const pngExists = await checkImageExists(pngPath);
    if (pngExists) {
      return pngPath;
    }
  }

  // Check each extension
  for (const ext of SUPPORTED_EXTENSIONS) {
    const imagePath = `${THUMBNAIL_BASE_PATH}${mappingKey}${suffix}.${ext}`;
    const exists = await checkImageExists(imagePath);
    if (exists) {
      return imagePath;
    }
  }
  
  return null;
};

/**
 * Article thumbnail mappings
 */
const THUMBNAIL_MAPPING: Record<string, string> = {
  'why-i-wrote-a-language': 'prism',
  'beyond-a-line-count': 'howmany',
  'rebuilding-earth': 'rebuilding-earth',
  'elevating-file-reuse-with-faxmachine-my-journey-to': 'faxmachine',
  'faxmachine-file-navigation': 'faxmachine',
  'why-i-built-safespace-and-why-its-changing-how-i-t': 'safespace',
  'safespace-testing-software': 'safespace',
  'qgit-versioning-simplified': 'qgit',
};

/**
 * Project thumbnail mappings
 * Only includes projects that have actual thumbnail files
 */
const PROJECT_THUMBNAIL_MAPPING: Record<string, string> = {
  'building-blocks': 'bb',
  'manifest': 'manifest',
  'sherlockio': 'sherlockio',
  'retrigger': 'retrigger',
  'callosum': 'callosum',
  'polyglot': 'polyglot',
  'wolfgang': 'wolfgang',
  'prism': 'prism',
  'howmany': 'howmany',
  'qvenv': 'qvenv',
  'qgit': 'qgit',
  'fax-machine': 'faxmachine',
  'generator': 'generator',
  'safespace': 'safespace',
  'desktidy': 'desktidy',
  'hephaestus': 'hephaestus',
  'grandmasters': 'grandmasters',
  'etymologistics': 'etymologistics',
  'psychoflight': 'psychoflight',
  'exporter': 'exporter',
  // Note: removed foundry, wikispace, and nexus as they no longer exist
};

/**
 * Get article thumbnail
 */
export const getThumbnail = async (articleId: string, emojiThumbnail: string) => {
  const imagePath = await findThumbnailPath(articleId, 'article');
  
  if (imagePath) {
    return {
      type: 'image' as const,
      src: imagePath
    };
  }
  
  return {
    type: 'emoji' as const,
    src: emojiThumbnail
  };
};

/**
 * Get project thumbnail
 */
export const getProjectThumbnail = async (projectId: string, emojiThumbnail: string) => {
  console.log(`🔍 Loading thumbnail for project: ${projectId}`);
  
  const imagePath = await findThumbnailPath(projectId, 'project');
  
  if (imagePath) {
    console.log(`✅ Found image thumbnail for ${projectId}: ${imagePath}`);
    return {
      type: 'image' as const,
      src: imagePath
    };
  }
  
  console.log(`❌ No image thumbnail found for ${projectId}, using emoji: ${emojiThumbnail}`);
  return {
    type: 'emoji' as const,
    src: emojiThumbnail
  };
};

// Legacy compatibility functions (now simplified)
export const checkThumbnailImage = (articleId: string) => findThumbnailPath(articleId, 'article');

// Simplified preloading - just returns immediately
export const preloadCriticalThumbnails = async (projectIds: string[], viewportInfo?: any) => {
  // No-op for simplicity
  return Promise.resolve();
};

export const preloadProjectThumbnails = preloadCriticalThumbnails;
export const preloadThumbnails = preloadCriticalThumbnails;

// Simplified stats
export const getThumbnailLoadingStats = () => {
  return {
    totalRequests: 0,
    cacheHits: 0,
    networkRequests: 0,
    averageLoadTime: 0,
    cacheHitRate: '0%',
    cachedImages: 0,
    activeLoads: 0,
    queuedLoads: 0
  };
};

// Simplified debug functions
export const clearThumbnailCache = () => {
  // No-op for simplicity
};

export const debugThumbnailPerformance = async () => {
  return getThumbnailLoadingStats();
}; 